import React, { createContext, useState, useEffect, useContext } from 'react';
import en from '../locales/eng';
import vi from '../locales/vie';
import de from '../locales/ger';
import jp from '../locales/jp';
import fr from '../locales/fr';

export const LanguageContext = createContext();

const translations = {
  eng: en,
  vie: vi,
  ger: de,
  jpn: jp,
  fra: fr
};

const pathToLang = {
  en: 'eng',
  vi: 'vie',
  de: 'ger',
  ja: 'jpn',
  fr: 'fra'
};

const langToPath = {
  eng: 'en',
  vie: 'vi',
  ger: 'de',
  jpn: 'ja',
  fra: 'fr'
};

const langToHtml = {
  eng: 'en',
  vie: 'vi',
  ger: 'de',
  jpn: 'ja',
  fra: 'fr'
};

const supportedPathLangs = Object.keys(pathToLang);
const siteOrigin = 'https://kentphotobooth.io.vn';

const getPathLang = () => window.location.pathname.split('/').filter(Boolean)[0]?.toLowerCase();

const getCurrentPathWithoutLang = () => {
  const parts = window.location.pathname.split('/').filter(Boolean);
  if (supportedPathLangs.includes(parts[0])) {
    parts.shift();
  }
  return parts.length ? `/${parts.join('/')}` : '/';
};

const getLangFromQuery = () => {
  const langParam = new URLSearchParams(window.location.search).get('lang')?.toLowerCase();
  if (!langParam) return null;
  if (langParam === 'vi' || langParam === 'vie') return 'vie';
  if (langParam === 'en' || langParam === 'eng') return 'eng';
  if (langParam === 'de' || langParam === 'ger') return 'ger';
  if (langParam === 'ja' || langParam === 'jpn') return 'jpn';
  if (langParam === 'fr' || langParam === 'fra') return 'fra';
  return null;
};

const buildLocalizedPath = (lang, includeHash = true) => {
  const langPath = langToPath[lang] || 'vi';
  const pathWithoutLang = getCurrentPathWithoutLang();
  const localizedPath = pathWithoutLang === '/' ? `/${langPath}/` : `/${langPath}${pathWithoutLang}`;
  return includeHash ? `${localizedPath}${window.location.hash}` : localizedPath;
};

export const getLanguageBasename = () => {
  const pathLang = getPathLang();
  return supportedPathLangs.includes(pathLang) ? `/${pathLang}` : '';
};

export const LanguageProvider = ({ children }) => {
  const [isSwitching, setIsSwitching] = useState(false);
  const [language, setLanguage] = useState(() => {
    try {
      const pathLang = getPathLang();
      if (pathToLang[pathLang]) {
        return pathToLang[pathLang];
      }

      const queryLang = getLangFromQuery();
      if (queryLang) {
        window.history.replaceState({}, '', buildLocalizedPath(queryLang));
        return queryLang;
      }
    } catch (e) {
      console.warn('URL language detection failed:', e);
    }

    let savedLang = null;
    try {
      savedLang = localStorage.getItem('app_lang');
    } catch (e) {
      console.warn('localStorage is blocked/disabled:', e);
    }

    if (savedLang && translations[savedLang]) {
      return savedLang;
    }

    try {
      const browserLang = navigator.language || navigator.userLanguage || '';
      const langCode = browserLang.split('-')[0].toLowerCase();

      if (langCode === 'vi') return 'vie';
      if (langCode === 'en') return 'eng';
      if (langCode === 'de') return 'ger';
      if (langCode === 'ja') return 'jpn';
      if (langCode === 'fr') return 'fra';
    } catch (e) {
      console.warn('Browser language detection failed:', e);
    }

    try {
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
      if (tz === 'Asia/Ho_Chi_Minh' || tz === 'Asia/Saigon') {
        return 'vie';
      }
    } catch (e) {
      console.warn('Timezone detection failed:', e);
    }

    return 'vie';
  });

  useEffect(() => {
    try {
      localStorage.setItem('app_lang', language);
    } catch (e) {
      console.warn('Failed to save language to localStorage:', e);
    }

    try {
      const langPath = langToPath[language] || 'vi';
      const pathLang = getPathLang();
      const canonicalPath = buildLocalizedPath(language, false);
      const canonicalUrl = `${siteOrigin}${canonicalPath}`;

      document.documentElement.lang = langToHtml[language] || 'vi';

      let canonical = document.querySelector('link[rel="canonical"]');
      if (!canonical) {
        canonical = document.createElement('link');
        canonical.setAttribute('rel', 'canonical');
        document.head.appendChild(canonical);
      }
      canonical.setAttribute('href', canonicalUrl);

      const ogUrl = document.querySelector('meta[property="og:url"]');
      if (ogUrl) {
        ogUrl.setAttribute('content', canonicalUrl);
      }

      // Only re-assign if we are NOT in the middle of a SPA transition
      // (This is a fallback for initial load/manual URL changes)
      if (!isSwitching && pathLang !== langPath) {
        if (pathLang && pathToLang[pathLang]) {
          // If the user manually changed the URL to a supported language, update the state
          setLanguage(pathToLang[pathLang]);
        } else {
          // If the URL has no language or an invalid one, redirect to the canonical localized path
          window.location.assign(canonicalPath);
        }
      }
    } catch (e) {
      console.warn('Failed to sync language to URL:', e);
    }
  }, [language, isSwitching]);

  // Sync state if user uses back/forward buttons
  useEffect(() => {
    const handlePopState = () => {
      const pathLang = getPathLang();
      if (pathLang && pathToLang[pathLang]) {
        const newLang = pathToLang[pathLang];
        if (newLang !== language) {
          setLanguage(newLang);
        }
      }
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [language]);

  const t = (key) => {
    const keys = key.split('.');
    let value = translations[language];
    for (const k of keys) {
      if (value && value[k] !== undefined) {
        value = value[k];
      } else {
        return key;
      }
    }
    return value;
  };

  const changeLanguage = async (lang) => {
    if (translations[lang] && lang !== language) {
      setIsSwitching(true);
      window.__isSwitchingLanguage = true;
      
      // Simulate progress bar start
      await new Promise(resolve => setTimeout(resolve, 50));
      
      const nextPath = buildLocalizedPath(lang, false);
      
      // Update URL first so getLanguageBasename() picks up the new path on next render
      window.history.pushState({}, '', nextPath);
      
      // Update state
      setLanguage(lang);
      
      // Wait for "loading" to finish
      await new Promise(resolve => setTimeout(resolve, 200));
      setIsSwitching(false);
      window.__isSwitchingLanguage = false;
    }
  };

  return (
    <LanguageContext.Provider value={{ language, t, changeLanguage, isSwitching }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => useContext(LanguageContext);
